import { useTranslation } from 'react-i18next';

import { DataPanel, DataPanelItem, DebugView, FaIcon, NgCopyButton } from '@/ui';
import { agencyIdEquals } from '@ngsos/portal-core/incidents';
import { toShortDate, toTime } from '@ngsos/portal-core/time';
import type { IncidentAgency, IncidentAgencyView, PortalCall } from '@/signalr';

export type IncidentCallsPanelProps = {
  involvedAgencies: readonly IncidentAgency[];
  agencyViews: readonly IncidentAgencyView[];
};

type AgencyCall = {
  agencyName: string;
  call: PortalCall;
};

function flattenAgencyCalls(
  involvedAgencies: readonly IncidentAgency[],
  agencyViews: readonly IncidentAgencyView[],
): AgencyCall[] {
  return agencyViews.flatMap((view) => {
    if (view.calls.length === 0) {
      return [];
    }

    const agency = involvedAgencies.find((candidate) => agencyIdEquals(candidate.agencyId, view.agencyId))!;
    return view.calls.map((call) => ({ agencyName: agency.displayName, call }));
  });
}

export function IncidentCallsPanel({ involvedAgencies, agencyViews }: IncidentCallsPanelProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const calls = flattenAgencyCalls(involvedAgencies, agencyViews);

  return (
    <DebugView>
      <DataPanel header={t('Incident_InfoPanel_Calls')}>
        {calls.map(({ agencyName, call }) => (
          <DataPanelItem
            key={call.id}
            customPanelTemplate={
              <div>
                <div className="d-flex align-items-center">
                  <FaIcon
                    icon={`${call.endTimeUtc == null ? 'fa-light fa-phone-volume' : 'fa-light fa-phone-hangup'} fa-fw`}
                    cssClass="me-1"
                    size="sm"
                  />
                  <span className="text-nowrap">
                    {toShortDate(call.startTimeUtc, locale)} {toTime(call.startTimeUtc, locale)}–
                    {call.endTimeUtc == null ? '…' : toTime(call.endTimeUtc, locale)}
                  </span>
                </div>
                <div className="small text-muted text-break">{agencyName}</div>
                <div className="small text-muted text-break">
                  ID: <code>{call.id}</code> <NgCopyButton valueToCopy={call.id} />
                </div>
              </div>
            }
          />
        ))}
      </DataPanel>
    </DebugView>
  );
}
